import { useMemo } from "react";
import type { ImmutableObject } from "@/lib/immutable";
import { createStore, type Store, useStore } from "./store";
import { type LocaleState, useLocaleState } from "./useLocaleState";

class ResetPasswordFormModel {
	isPopup: boolean = false;
	isWaitingForSendResetPasswordEmail: boolean = false;

	sendResetPasswordEmailErrorMessage: string = "";
	isSendResetPasswordEmailSuccessful: boolean = false;

	isValid: boolean = false;
	showValidationError = false;

	emailValue: string = "";
	emailError: string = "";
	emailRequiredError: string = "";
	emailIsDisabled: boolean = false;

	temporaryPasswordValue: string = "";
	temporaryPasswordError: string = "";
	temporaryPasswordRequiredError: string = "";

	newPasswordValue: string = "";
	newPasswordError: string = "";
	newPasswordRequiredError: string = "";
}

const initialState = new ResetPasswordFormModel();
const resetPasswordFormGlobalStore =
	createStore<ResetPasswordFormModel>(initialState);

export class ResetPasswordFormState {
	private resetPasswordFormState: Store<ResetPasswordFormModel>;
	private localeState: LocaleState;

	get(): ImmutableObject<ResetPasswordFormModel> {
		return this.resetPasswordFormState.getState();
	}

	constructor(
		resetPasswordFormState: Store<ResetPasswordFormModel>,
		localeState: LocaleState,
	) {
		this.resetPasswordFormState = resetPasswordFormState;
		this.localeState = localeState;
	}

	reset() {
		this.resetPasswordFormState.setState(new ResetPasswordFormModel());
	}

	validateForm(): void {
		// validate the login form, triggering a mutation to the newLoginState only if it needs to change
		const newResetPasswordFormState: Partial<ResetPasswordFormModel> = {};

		// validate email
		const currentState = this.resetPasswordFormState.getState();
		const emailHasError = !currentState.emailValue;
		if (emailHasError !== !!currentState.emailError) {
			newResetPasswordFormState.emailError = currentState.emailRequiredError;
		}

		// update isValid and showValidationError states
		const isValid = !emailHasError;
		if (isValid !== currentState.isValid) {
			newResetPasswordFormState.isValid = isValid;
			newResetPasswordFormState.showValidationError =
				newResetPasswordFormState.isValid;
		}

		// update the login form state
		if (Object.keys(newResetPasswordFormState).length > 0) {
			this.resetPasswordFormState.setState((prev) => ({
				...prev,
				...newResetPasswordFormState,
			}));
		}
	}

	// translated with https://www.deepl.com/translator
	translate(text: string): string {
		const language = this.localeState.get().language;
		switch (text) {
			case "Reset Password":
				switch (language) {
					case "es":
						return "Restablecer contraseña";
					case "fr":
						return "Réinitialiser le mot de passe";
					default:
						return text;
				}
			case "Complete Registration":
				switch (language) {
					case "es":
						return "Completar la inscripción";
					case "fr":
						return "Compléter l'inscription";
					default:
						return text;
				}
			case "Change Password":
				switch (language) {
					case "es":
						return "Cambiar contraseña";
					case "fr":
						return "Modifier le mot de passe";
					default:
						return text;
				}
			case "Enter a password to complete your registration.":
				switch (language) {
					case "es":
						return "Introduzca una contraseña para completar su registro.";
					case "fr":
						return "Saisissez un mot de passe pour terminer votre inscription.";
					default:
						return text;
				}
			case "Enter your new password.":
				switch (language) {
					case "es":
						return "Introduzca su nueva contraseña.";
					case "fr":
						return "Entrez votre nouveau mot de passe.";
					default:
						return text;
				}
			case "E-mail":
				switch (language) {
					case "es":
						return "Correo electrónico";
					case "fr":
						return "E-mail";
					default:
						return text;
				}
			case "E-mail is required":
				switch (language) {
					case "es":
						return "Correo electrónico obligatorio";
					case "fr":
						return "L'e-mail est requis";
					default:
						return text;
				}
			case "New Password":
				switch (language) {
					case "es":
						return "Nueva contraseña";
					case "fr":
						return "Nouveau mot de passe";
					default:
						return text;
				}
			case "Reset password email was sent to":
				switch (language) {
					case "es":
						return "Se ha enviado un correo electrónico de restablecimiento de contraseña a";
					case "fr":
						return "Le courriel de réinitialisation du mot de passe a été envoyé à";
					default:
						return text;
				}
			case "Change password email was sent to":
				switch (language) {
					case "es":
						return "Se ha enviado un correo electrónico de cambio de contraseña a";
					case "fr":
						return "L'email de changement de mot de passe a été envoyé à";
					default:
						return text;
				}
			case "Sending reset password email failed":
				switch (language) {
					case "es":
						return "Error al enviar el correo electrónico de restablecimiento de contraseña";
					case "fr":
						return "L'envoi de l'e-mail de réinitialisation du mot de passe a échoué";
					default:
						return text;
				}
			case "Enter your email address and click the 'Reset Password' button to receive an email with an active link to reset your password.":
				switch (language) {
					case "es":
						return "Introduzca su dirección de correo electrónico y haga clic en el botón 'Restablecer contraseña' para recibir un correo electrónico con un enlace activo para restablecer su contraseña.";
					case "fr":
						return "Saisissez votre adresse électronique et cliquez sur le bouton 'Réinitialiser le mot de passe' pour recevoir un courriel contenant un lien actif vous permettant de réinitialiser votre mot de passe.";
					default:
						return text;
				}
			case "Reset password was sucessful.":
				switch (language) {
					case "es":
						return "Se ha podido restablecer la contraseña.";
					case "fr":
						return "La réinitialisation du mot de passe a réussi.";
					default:
						return text;
				}
			default:
				return text;
		}
	}

	setEmail(value: string): void {
		this.resetPasswordFormState.setState((prev) => ({
			...prev,
			emailValue: value,
		}));
	}

	setTemporaryPassword(value: string): void {
		this.resetPasswordFormState.setState((prev) => ({
			...prev,
			temporaryPasswordValue: value,
		}));
	}

	setNewPassword(value: string): void {
		this.resetPasswordFormState.setState((prev) => ({
			...prev,
			newPasswordValue: value,
		}));
	}

	setIsPopup(value: boolean): void {
		this.resetPasswordFormState.setState((prev) => ({
			...prev,
			isPopup: value,
		}));
	}

	setError(error: unknown) {
		if (typeof error === "string") {
			this.resetPasswordFormState.setState((prev) => ({
				...prev,
				sendResetPasswordEmailErrorMessage: error,
			}));
		} else if (typeof error === "object") {
			this.resetPasswordFormState.setState((prev) => ({
				...prev,
				sendResetPasswordEmailErrorMessage: this.translate(
					"Sending reset password email failed",
				),
			}));
			console.error(error);
		}
	}

	setIsWaitingForSendResetPasswordEmail(value: boolean) {
		this.resetPasswordFormState.setState((prev) => ({
			...prev,
			isWaitingForSendResetPasswordEmail: value,
		}));
	}
}

export function useResetPasswordFormState(): ResetPasswordFormState {
	const localeState = useLocaleState();
	useStore(resetPasswordFormGlobalStore);
	return useMemo(
		() => new ResetPasswordFormState(resetPasswordFormGlobalStore, localeState),
		[localeState],
	);
}
